import { FormEvent, useEffect, useRef, useState } from "react";
import { Contact, Task } from "../../../types/TradeFlow";

import { CrossIcon, UserIcon } from "../../Icons";
import { getStringColor, today } from "../../../utils";
import { useTradeFlowData } from "../../../context/TradeFlowDataContext ";
import { useTradeFlowTracker } from "../../../context/TradeFlowTrackerContext";

const initialTask = {
  title: "",
  task: "",
  date: "",
  assign_to: { name: "", email: "" },
};

export default function Tasks() {
  const { isViewer } = useTradeFlowTracker();
  const { tradeFlowData, updateTradeFlow } = useTradeFlowData();

  const { trade_flow_json, id, row_id } = tradeFlowData;

  const handleAddTask = async (index: number, task: Task) => {
    if (isViewer) return;

    const updatedSteps = trade_flow_json.map((step, i) =>
      i === index ? { ...step, tasks: [...(step.tasks || []), task] } : step
    );

    await updateTradeFlow({ id, row_id, trade_flow_json: updatedSteps });
  };

  const handleSaveTask = async (
    stepIndex: number,
    taskIndex: number,
    updatedTask: Task
  ) => {
    const updatedSteps = trade_flow_json.map((step, i) => {
      if (i === stepIndex) {
        const updated = (step.tasks || []).map((task: Task, j: number) =>
          j === taskIndex ? updatedTask : task
        );
        return { ...step, tasks: updated };
      }
      return step;
    });

    await updateTradeFlow({ id, row_id, trade_flow_json: updatedSteps });
  };

  const handleRemoveTask = async (stepIndex: number, taskIndex: number) => {
    const updatedSteps = trade_flow_json.map((step, i) =>
      i === stepIndex
        ? {
            ...step,
            tasks: (step.tasks || []).filter((_, j) => j !== taskIndex),
          }
        : step
    );

    await updateTradeFlow({ id, row_id, trade_flow_json: updatedSteps });
  };

  const allContacts = trade_flow_json
    .flatMap((step) => step.Contacts)
    .filter((c) => c.name?.trim() && c.email?.trim());

  return (
    <section className="w-full flex justify-start items-start gap-3 text-xs h-full overflow-auto will-change-transform">
      {trade_flow_json.map((el, idx) => (
        <div
          key={el.step_id}
          className="relative min-w-[200px] max-w-[150px] flex-shrink-0"
        >
          <div className="w-full h-[100px] flex flex-col items-center justify-start text-center text-gray-900 gap-2">
            <h4 className="size-8 text-lg bg-white z-10 flex items-center justify-center p-1 border-2 border-gray-900 rounded-full">
              {idx + 1}
            </h4>
            <p className="font-medium">{el.step.field}</p>
          </div>

          {idx < trade_flow_json.length - 1 && (
            <div className="absolute top-4 right-[-100px] w-full h-[1px] bg-gray-900" />
          )}
          {idx !== 0 && (
            <div className="absolute top-4 left-[-100px] w-full h-[1px] bg-gray-900" />
          )}

          <div className="space-y-2">
            {(el.tasks || []).map((task: Task, i: number) => (
              <div
                key={i}
                className="relative p-2 text-gray-500 space-y-2 border shadow-sm rounded-xl"
              >
                {!isViewer && (
                  <button
                    type="button"
                    className="absolute -top-[2px] -right-[4px] p-[1px] bg-gray-300 rounded-full hover:bg-red-500 hover:text-white hover:scale-90"
                    onClick={() => handleRemoveTask(idx, i)}
                  >
                    <CrossIcon className="size-4" />
                  </button>
                )}

                <EditableTaskForm
                  stepIndex={idx}
                  taskIndex={i}
                  task={task}
                  allContacts={allContacts}
                  handleSaveTask={handleSaveTask}
                  isViewer={isViewer}
                />
              </div>
            ))}

            <button
              disabled={isViewer}
              title={isViewer ? "You don’t have permission to edit" : undefined}
              className="
                w-full px-2 py-1 font-medium rounded-full border border-dashed
                bg-gray-100 text-gray-700
                disabled:bg-gray-200
                disabled:text-gray-400
                disabled:cursor-not-allowed
              "
              onClick={() => handleAddTask(idx, initialTask)}
            >
              + Add Task
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}

type TaskProps = {
  stepIndex: number;
  taskIndex: number;
  task: Task;
  allContacts: Contact[];
  handleSaveTask: (
    stepIndex: number,
    taskIndex: number,
    updatedTask: Task
  ) => void;
  isViewer: boolean;
};

type FieldKey = "title" | "task" | "date" | "assign_to";

function EditableTaskForm({
  task,
  stepIndex,
  taskIndex,
  allContacts,
  handleSaveTask,
  isViewer,
}: TaskProps) {
  const [isEditable, setIsEditable] = useState(false);
  const [focusField, setFocusField] = useState<FieldKey | null>(null);

  const [isAssignToEditable, setIsAssignToEditable] = useState(false);
  const [assignToQuery, setAssignToQuery] = useState("");
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // store selected person (but NOT saved yet)
  const [selectedAssignTo, setSelectedAssignTo] = useState<
    Task["assign_to"] | null
  >(null);

  const formRef = useRef<HTMLFormElement>(null);
  const assignToWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditable && focusField && formRef.current) {
      const input = formRef.current.querySelector<
        HTMLInputElement | HTMLTextAreaElement
      >(`[name="${focusField}"]`);
      input?.focus();
    }

    if (isAssignToEditable && focusField && formRef.current) {
      const input = formRef.current.querySelector<
        HTMLInputElement | HTMLTextAreaElement
      >(`[name="${focusField}"]`);
      input?.focus();
    }
  }, [isEditable, isAssignToEditable, focusField]);

  // close suggestions on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        assignToWrapperRef.current &&
        !assignToWrapperRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
        setIsAssignToEditable(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEdit = (field: FieldKey) => {
    if (isViewer) return;

    setFocusField(field);
    setIsEditable(true);
  };

  const handleAssignToEdit = (field: FieldKey) => {
    if (isViewer) return;

    setFocusField(field);
    setIsAssignToEditable(true);
  };

  const handleAssignToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAssignToQuery(value);

    setFilteredContacts(
      value.trim()
        ? allContacts.filter(
            (c) =>
              c.name.toLowerCase().includes(value.toLowerCase()) ||
              c.email.toLowerCase().includes(value.toLowerCase())
          )
        : allContacts
    );

    setShowSuggestions(true);
  };

  const selectContact = (c: Contact) => {
    setSelectedAssignTo({ name: c.name, email: c.email });
    setAssignToQuery("");
    setShowSuggestions(false);
    setIsAssignToEditable(false);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const raw = Object.fromEntries(fd);

    const merged: Task = {
      title: raw.title?.toString() ?? task.title,
      task: raw.task?.toString() ?? task.task,
      date: raw.date?.toString() ?? task.date,

      // priority: 1) local selectedAssignTo 2) existing task.assign_to
      assign_to:
        selectedAssignTo ??
        (task.assign_to?.name && task.assign_to?.email
          ? task.assign_to
          : { name: "", email: "" }),
    };

    handleSaveTask(stepIndex, taskIndex, merged);
    setIsEditable(false);
    setIsAssignToEditable(false);
  };

  return (
    <form
      className="w-full space-y-2 relative"
      ref={formRef}
      onSubmit={handleSubmit}
    >
      {/* MAIN FIELDS */}
      {isEditable ? (
        <>
          <input
            className="w-full outline-none"
            type="text"
            name="title"
            placeholder="Enter title"
            defaultValue={task.title}
            required
          />

          <textarea
            className="w-full outline-none text-xs"
            name="task"
            placeholder="Enter task details"
            defaultValue={task.task}
            rows={3}
          />

          <input
            className="w-full outline-none cursor-pointer"
            type="date"
            name="date"
            defaultValue={task.date}
            onFocus={(e) => e.target.showPicker()}
            min={today}
          />
        </>
      ) : (
        <div className="space-y-2 overflow-hidden">
          <p
            onClick={() => handleEdit("title")}
            className={task.title ? "font-medium text-gray-800 truncate" : ""}
          >
            {task.title || "Enter title"}
          </p>

          <p onClick={() => handleEdit("task")} className="line-clamp-3">
            {task.task || "Enter task description"}
          </p>

          <p onClick={() => handleEdit("date")}>{task.date || "Pick date"}</p>
        </div>
      )}

      {/* ASSIGN TO */}
      {isAssignToEditable ? (
        <div className="relative" ref={assignToWrapperRef}>
          <input
            type="text"
            name="assign_to"
            className="w-full outline-none"
            placeholder="Assign to"
            value={assignToQuery}
            onChange={handleAssignToChange}
            onFocus={() => {
              setFilteredContacts(allContacts);
              setShowSuggestions(true);
            }}
            autoComplete="off"
          />

          {showSuggestions && (
            <ul
              className="absolute top-full left-0 right-0 z-30 mt-1 bg-white border
               border-gray-200 rounded-xl shadow-lg overflow-hidden
               max-h-60 overflow-y-auto
               animate-in fade-in-0 zoom-in-95 duration-100"
            >
              {filteredContacts.length === 0 ? (
                <li className="px-4 py-3 text-sm text-gray-500 text-center">
                  No contacts found
                </li>
              ) : (
                filteredContacts.map((c) => (
                  <li
                    key={c.contact_id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer
                    hover:bg-gray-50 transition-colors
                    border-b border-gray-100 last:border-b-0"
                    onClick={() => selectContact(c)}
                  >
                    <ContactInfo contact={c} />
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      ) : (
        <div
          onClick={() => {
            if (isViewer) return;

            setIsEditable(true);
            handleAssignToEdit("assign_to");
          }}
          className={`flex items-center gap-2 p-1 ${
            isViewer ? "cursor-text" : "cursor-pointer"
          } rounded-md
             hover:bg-gray-50 transition-colors text-gray-700 overflow-hidden`}
        >
          {selectedAssignTo ? (
            // 2️⃣ SHOW LOCALLY-SELECTED CONTACT BEFORE SAVING
            <ContactInfo contact={selectedAssignTo} />
          ) : task.assign_to?.name ? (
            // 3️⃣ SHOW SAVED DATA
            <ContactInfo contact={task.assign_to} />
          ) : (
            // 1️⃣ INITIAL EMPTY STATE
            <p className="text-gray-500 text-xs inline-flex items-center gap-1">
              <UserIcon className="size-3" />
              Assign to
            </p>
          )}
        </div>
      )}

      {isEditable ? (
        <button className="w-full py-1 bg-custom-blue-medium text-white rounded-xl">
          Save
        </button>
      ) : null}
    </form>
  );
}

export function ContactInfo({
  contact,
}: {
  contact: Pick<Contact, "name" | "email">;
}) {
  return (
    <>
      {/* Avatar */}
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center
                   text-white font-medium text-sm shadow-sm shrink-0"
        style={{ backgroundColor: getStringColor(contact.name) }}
      >
        {contact.name?.charAt(0)?.toUpperCase()}
      </div>

      {/* Name + Email */}
      <div className="leading-tight">
        <p className="text-xs font-medium text-gray-900 truncate max-w-[120px]">
          {contact.name}
        </p>

        <p className="text-[10px] text-gray-500 truncate max-w-[120px]">
          {contact.email}
        </p>
      </div>
    </>
  );
}
